import api from 'services/api';
import { array, enforce, num, nullable, object, runSchema, str, unknownRecord } from 'validation';

/**
 * A single scheduling window as returned by nap-backend
 * `GET /scheduling_windows/:id` (routes/scheduling_windows.js → windowDetailsSQL
 * + `folder_path`). Re-derived from the backend source, NOT the
 * `scheduling_windowsSlice` `SchedulingWindow` type, which drifted badly: it
 * declares `schedules`/`references` as the UI shapes (the wire is raw
 * snake_case that the modal converts), plus `exclusions`/`is_calendar`/
 * `day_rollover`/`min_open_duration_ms` that this endpoint never returns. The
 * endpoint also returns `id`, `created_at`, `updated_at` and `folder_path`,
 * which the modal does not read, so the object parser drops them. Deeply
 * readonly — validated wire data.
 */
export interface FetchedSchedulingWindow {
  readonly scheduling_window_id: number;
  readonly name: string;
  readonly description: string;
  readonly timezone: string;
  readonly zone_name: string;
  readonly folder_id: number | null;
  readonly schedules: readonly Record<string, unknown>[];
  readonly references: readonly Record<string, unknown>[];
}

const schedulingWindowSchema = object<FetchedSchedulingWindow>({
  scheduling_window_id: num(),
  name: str(),
  description: str(),
  timezone: str(),
  zone_name: str(),
  folder_id: nullable(num()),
  // Dynamic rule objects the modal re-parses (apiScheduleToUi / apiRefsToUiLinks);
  // the server is the validation authority for their internals. Each element is
  // validated only as "an object" (contents pass through verbatim); a malformed
  // (non-object) element is dropped rather than handed to the modal to crash on.
  schedules: array(unknownRecord()),
  references: array(unknownRecord()),
});

/**
 * Fetch a single scheduling window by id for the edit form when the list slice
 * is empty (e.g. launched from the Workspace). Boundary-validated (issue #757
 * step 3); structural failure or a drifted identity (`scheduling_window_id`)
 * yields null so the caller surfaces a "not found" instead of opening the modal
 * on a degraded record.
 */
export const getSchedulingWindow = async (id: number): Promise<FetchedSchedulingWindow | null> => {
  try {
    const res = await api.get(`/scheduling_windows/${id}`);
    const result = runSchema(schedulingWindowSchema, res.data, 'getSchedulingWindow');
    if (!result.ok) return null;
    return enforce(result.value, result.warnings, ['scheduling_window_id']);
  } catch {
    return null;
  }
};
